class AtlasProductGallery extends HTMLElement {
  constructor() {
    super();
    this.sectionId = this.dataset.sectionId;
    this.mainEl = null;
    this.thumbsEl = null;
    this.prevBtn = null;
    this.nextBtn = null;
    this.lightboxEl = null;
    this.lightboxMainEl = null;
    this.lightboxPrev = null;
    this.lightboxNext = null;
    this.lightboxClose = null;
    this.lightboxCounterCurrent = null;
    this.mainSwiper = null;
    this.thumbsSwiper = null;
    this.lightboxSwiper = null;
    this.isLightboxBound = false;
    this.onResize = this.onResize.bind(this);
    this.onLightboxTrigger = this.onLightboxTrigger.bind(this);
    this.onEscape = this.onEscape.bind(this);
    this.closeLightbox = this.closeLightbox.bind(this);
  }

  get enableZoom() {
    return this.dataset.enableZoom !== "false";
  }

  connectedCallback() {
    this.cacheElements();
    if (!this.mainEl) return;

    if (this.enableZoom) {
      this.mountLightbox();
    }

    this.waitForSwiper().then(() => {
      this.initSwipers();
      if (this.enableZoom) {
        this.bindLightbox();
      }
      this.bindVideos();
    });

    window.addEventListener("resize", this.onResize);
  }

  disconnectedCallback() {
    window.removeEventListener("resize", this.onResize);
    document.removeEventListener("keydown", this.onEscape);
    this.destroySwipers();
    this.destroyLightboxSwiper();
    document.body.classList.remove("atlas-gallery-lightbox-open");
  }

  cacheElements() {
    this.mainEl = this.querySelector("[data-gallery-main]");
    this.thumbsEl = this.querySelector("[data-gallery-thumbs]");
    this.prevBtn = this.querySelector("[data-gallery-prev]");
    this.nextBtn = this.querySelector("[data-gallery-next]");
    this.lightboxEl = this.querySelector("[data-gallery-lightbox]");
    this.lightboxMainEl = this.querySelector("[data-gallery-lightbox-main]");
    this.lightboxPrev = this.querySelector("[data-gallery-lightbox-prev]");
    this.lightboxNext = this.querySelector("[data-gallery-lightbox-next]");
    this.lightboxClose = this.querySelector("[data-gallery-lightbox-close]");
    this.lightboxCounterCurrent = this.querySelector(
      "[data-gallery-lightbox-counter-current]",
    );
  }

  mountLightbox() {
    if (!this.lightboxEl || this.lightboxEl.dataset.mounted === "true") return;

    const galleryStyles = getComputedStyle(this);
    const mediaBg = galleryStyles.getPropertyValue("--gallery-media-bg").trim();

    if (mediaBg) {
      this.lightboxEl.style.setProperty("--gallery-media-bg", mediaBg);
    }

    document.body.appendChild(this.lightboxEl);
    this.lightboxEl.dataset.mounted = "true";
  }

  waitForSwiper() {
    return new Promise((resolve) => {
      if (typeof Swiper !== "undefined") {
        resolve();
        return;
      }

      const checkSwiper = () => {
        if (typeof Swiper !== "undefined") {
          resolve();
          return;
        }

        requestAnimationFrame(checkSwiper);
      };

      checkSwiper();
    });
  }

  destroySwipers() {
    this.mainSwiper?.destroy(true, true);
    this.thumbsSwiper?.destroy(true, true);
    this.mainSwiper = null;
    this.thumbsSwiper = null;
  }

  destroyLightboxSwiper() {
    this.lightboxSwiper?.destroy(true, true);
    this.lightboxSwiper = null;
  }

  getThumbDirection() {
    return window.matchMedia("(min-width: 769px)").matches
      ? "vertical"
      : "horizontal";
  }

  initSwipers() {
    this.destroySwipers();

    const isDesktop = window.matchMedia("(min-width: 769px)").matches;

    if (this.thumbsEl) {
      this.thumbsSwiper = new Swiper(this.thumbsEl, {
        direction: this.getThumbDirection(),
        slidesPerView: "auto",
        spaceBetween: isDesktop ? 10 : 5,
        freeMode: true,
        watchSlidesProgress: true,
        slideToClickedSlide: true,
      });
    }

    this.mainSwiper = new Swiper(this.mainEl, {
      slidesPerView: 1,
      spaceBetween: 0,
      speed: 400,
      allowTouchMove: true,
      navigation: {
        prevEl: this.prevBtn,
        nextEl: this.nextBtn,
      },
      thumbs: this.thumbsSwiper
        ? {
            swiper: this.thumbsSwiper,
          }
        : undefined,
      on: {
        slideChange: () => {
          this.pauseMainVideos();
        },
      },
    });

    this.setActiveThumb(this.mainSwiper.activeIndex);
    this.mainSwiper.on("slideChange", () => {
      this.setActiveThumb(this.mainSwiper.activeIndex);
    });
  }

  onResize() {
    this.initSwipers();

    if (this.lightboxSwiper && !this.lightboxEl?.hidden) {
      this.lightboxSwiper.update();
    }
  }

  setActiveThumb(index) {
    this.querySelectorAll("[data-gallery-thumb]").forEach(
      (thumb, thumbIndex) => {
        thumb.classList.toggle("is-active", thumbIndex === index);
        thumb.setAttribute(
          "aria-current",
          thumbIndex === index ? "true" : "false",
        );
      },
    );
  }

  getSlideIndex(slide) {
    if (!slide || !this.mainEl) return 0;

    return Array.from(this.mainEl.querySelectorAll(".swiper-slide")).indexOf(
      slide,
    );
  }

  bindLightbox() {
    if (this.isLightboxBound) return;

    this.querySelectorAll("[data-gallery-zoom-trigger]").forEach((trigger) => {
      trigger.addEventListener("click", this.onLightboxTrigger);
    });

    this.lightboxClose?.addEventListener("click", this.closeLightbox);
    this.lightboxEl?.addEventListener("click", (event) => {
      if (event.target === this.lightboxEl) this.closeLightbox();
    });

    document.addEventListener("keydown", this.onEscape);
    this.isLightboxBound = true;
  }

  onEscape(event) {
    if (event.key === "Escape") this.closeLightbox();

    if (
      this.lightboxEl?.hidden === false &&
      (event.key === "ArrowLeft" || event.key === "ArrowRight")
    ) {
      if (event.key === "ArrowLeft") this.lightboxSwiper?.slidePrev();
      if (event.key === "ArrowRight") this.lightboxSwiper?.slideNext();
    }
  }

  onLightboxTrigger(event) {
    event.preventDefault();

    const trigger = event.currentTarget;
    const mediaType = trigger.dataset.mediaType;

    if (mediaType === "video" || mediaType === "external_video") {
      const slide = trigger.closest(".swiper-slide");
      this.openLightbox(this.getSlideIndex(slide));
      return;
    }

    const slide = trigger.closest(".swiper-slide");
    this.openLightbox(this.getSlideIndex(slide));
  }

  initLightboxSwiper(initialIndex = 0) {
    if (!this.lightboxMainEl) return;

    this.destroyLightboxSwiper();

    this.lightboxSwiper = new Swiper(this.lightboxMainEl, {
      slidesPerView: 1,
      spaceBetween: 0,
      speed: 400,
      initialSlide: initialIndex,
      allowTouchMove: true,
      navigation:
        this.lightboxPrev && this.lightboxNext
          ? {
              prevEl: this.lightboxPrev,
              nextEl: this.lightboxNext,
            }
          : undefined,
      on: {
        slideChange: () => {
          this.pauseLightboxMedia();
          this.activateLightboxSlide(this.lightboxSwiper.activeIndex);
          this.updateLightboxCounter();
        },
      },
    });
  }

  openLightbox(index = 0) {
    if (!this.lightboxEl) return;

    if (!this.lightboxSwiper) {
      this.initLightboxSwiper(index);
    } else {
      this.lightboxSwiper.slideTo(index, 0);
    }

    this.lightboxEl.hidden = false;
    document.body.classList.add("atlas-gallery-lightbox-open");
    this.pauseMainVideos();
    this.activateLightboxSlide(index);
    this.updateLightboxCounter();
  }

  activateLightboxSlide(index) {
    if (!this.lightboxMainEl) return;

    const slides = this.lightboxMainEl.querySelectorAll(".swiper-slide");

    slides.forEach((slide, slideIndex) => {
      const isActive = slideIndex === index;
      const mediaType = slide.dataset.mediaType;

      if (mediaType === "video") {
        const video = slide.querySelector("video");

        if (video) {
          if (isActive) {
            video.play().catch(() => {});
          } else {
            video.pause();
            video.currentTime = 0;
          }
        }
      }

      if (mediaType === "external_video" && isActive) {
        this.loadLightboxExternalVideo(slide);
      }
    });
  }

  loadLightboxExternalVideo(slide) {
    const container = slide.querySelector("[data-lightbox-external-video]");
    if (!container || container.dataset.videoLoaded === "true") return;

    const template = container.querySelector("template");
    if (!template) return;

    container.dataset.videoLoaded = "true";
    container.insertAdjacentHTML("beforeend", template.innerHTML);
  }

  updateLightboxCounter() {
    if (!this.lightboxCounterCurrent || !this.lightboxSwiper) return;

    this.lightboxCounterCurrent.textContent = String(
      this.lightboxSwiper.activeIndex + 1,
    );
  }

  pauseLightboxMedia() {
    this.lightboxMainEl?.querySelectorAll("video").forEach((video) => {
      video.pause();
      video.currentTime = 0;
    });
  }

  closeLightbox() {
    if (!this.lightboxEl || this.lightboxEl.hidden) return;

    this.pauseLightboxMedia();
    this.lightboxEl.hidden = true;
    document.body.classList.remove("atlas-gallery-lightbox-open");
  }

  bindVideos() {
    this.querySelectorAll("[data-gallery-video-play]").forEach((button) => {
      button.addEventListener("click", (event) => {
        event.preventDefault();
        event.stopPropagation();

        const slide = button.closest(".swiper-slide");
        const slideIndex = this.getSlideIndex(slide);

        if (this.enableZoom) {
          this.openLightbox(slideIndex);
          return;
        }

        const trigger = slide?.querySelector(
          ".atlas-main-product-gallery__media-trigger",
        );
        const video = slide?.querySelector("video");

        if (video) {
          trigger?.classList.add("is-media-playing");
          button.hidden = true;
          video.play().catch(() => {});
        }
      });
    });
  }

  pauseMainVideos() {
    this.querySelectorAll("[data-gallery-main] video").forEach((video) => {
      if (!video.paused) {
        video.pause();
      }
    });

    this.querySelectorAll("[data-gallery-zoom-trigger]").forEach((trigger) => {
      trigger.classList.remove("is-media-playing");
    });

    this.querySelectorAll("[data-gallery-video-play]").forEach((button) => {
      button.hidden = false;
    });
  }
}

if (!customElements.get("atlas-product-gallery")) {
  customElements.define("atlas-product-gallery", AtlasProductGallery);
}
